import React from 'react';
import styles from './MosaicImage.module.css';
import { MAX_DISPLAY_IMAGE_WIDTH, MOSAIC_TILE_COUNT } from '../../constants/game';
import { ImageDifficultySettings } from '../../types/difficulty';

interface MosaicImageProps {
  imageUrl: string;
  filterCss: string;
  tileOrder: number[];
  imageDimensions: { width: number; height: number } | null;
  onImageLoad: (dimensions: { width: number; height: number }) => void;
  onTileLoad: () => void;
  difficultySettings: ImageDifficultySettings;
}

const MosaicImage: React.FC<MosaicImageProps> = ({
  imageUrl,
  filterCss,
  tileOrder,
  imageDimensions,
  onImageLoad,
  onTileLoad,
  difficultySettings,
}) => {
  const { isCropped, cropPercentage, isFlipped } = difficultySettings;
  React.useEffect(() => {
    if (!imageDimensions) {
      const img = new Image();
      img.src = imageUrl;
      img.onload = () => {
        onImageLoad({ width: img.naturalWidth, height: img.naturalHeight });
      };
    }
  }, [imageDimensions, imageUrl, onImageLoad]);

  if (!imageDimensions) {
    return (
      <img
        src={imageUrl}
        alt="Guess the artist"
        style={filterCss ? { maxWidth: MAX_DISPLAY_IMAGE_WIDTH, filter: filterCss } : { maxWidth: MAX_DISPLAY_IMAGE_WIDTH }}
        onLoad={(e) => onImageLoad({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })}
      />
    );
  }

  const naturalWidth = imageDimensions.width;
  const naturalHeight = imageDimensions.height;
  const scale = naturalWidth > MAX_DISPLAY_IMAGE_WIDTH ? MAX_DISPLAY_IMAGE_WIDTH / naturalWidth : 1;
  const displayWidth = naturalWidth * scale;
  const displayHeight = naturalHeight * scale;
  const tileWidth = displayWidth / MOSAIC_TILE_COUNT;
  const tileHeight = displayHeight / MOSAIC_TILE_COUNT;

  const gridStyle: React.CSSProperties = {
    width: displayWidth,
    height: displayHeight,
    gridTemplateColumns: `repeat(${MOSAIC_TILE_COUNT}, ${tileWidth}px)`,
    gridTemplateRows: `repeat(${MOSAIC_TILE_COUNT}, ${tileHeight}px)`,
  };

  const tileImageStyle: React.CSSProperties = {
    width: naturalWidth,
    height: naturalHeight,
    ...(filterCss ? { filter: filterCss } : {}),
  };

  // If cropped, wrap the mosaic in a cropped container
  if (isCropped) {
    const showPercentage = 100 - cropPercentage;
    const cropSize = (displayWidth * showPercentage) / 100;
    
    return (
      <div
        style={{
          width: cropSize,
          height: cropSize,
          overflow: 'hidden',
          position: 'relative',
          maxWidth: '100%',
          maxHeight: '100%',
        }}
      >
        <div
          className={`${styles.mosaicGrid} ${isFlipped ? styles.flipped : ''}`}
          style={{
            ...gridStyle,
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        >
          {tileOrder.map((sourceIndex, tilePosition) => {
            const sourceRow = Math.floor(sourceIndex / MOSAIC_TILE_COUNT);
            const sourceCol = sourceIndex % MOSAIC_TILE_COUNT;
            const displayOffsetX = -(sourceCol * tileWidth);
            const displayOffsetY = -(sourceRow * tileHeight);
            const transform = `scale(${scale}) translate(${displayOffsetX / scale}px, ${displayOffsetY / scale}px)`;

            return (
              <div className={styles.mosaicTile} key={`${tilePosition}-${sourceIndex}`} style={{ width: tileWidth, height: tileHeight }}>
                <div
                  className={styles.tileInner}
                  style={{
                    width: tileWidth,
                    height: tileHeight,
                    overflow: 'hidden',
                  }}
                >
                  <img
                    src={imageUrl}
                    alt="Guess the artist"
                    style={{
                      ...tileImageStyle,
                      transform,
                      transformOrigin: 'top left',
                    }}
                    onLoad={tilePosition === 0 ? onTileLoad : undefined}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${styles.mosaicGrid} ${isFlipped ? styles.flipped : ''}`}
      style={gridStyle}
    >
      {tileOrder.map((sourceIndex, tilePosition) => {
        const sourceRow = Math.floor(sourceIndex / MOSAIC_TILE_COUNT);
        const sourceCol = sourceIndex % MOSAIC_TILE_COUNT;
        const displayOffsetX = -(sourceCol * tileWidth);
        const displayOffsetY = -(sourceRow * tileHeight);
        const transform = `scale(${scale}) translate(${displayOffsetX / scale}px, ${displayOffsetY / scale}px)`;

        return (
          <div className={styles.mosaicTile} key={`${tilePosition}-${sourceIndex}`} style={{ width: tileWidth, height: tileHeight }}>
            <div
              className={styles.tileInner}
              style={{
                width: tileWidth,
                height: tileHeight,
                overflow: 'hidden',
              }}
            >
              <img
                src={imageUrl}
                alt="Guess the artist"
                style={{
                  ...tileImageStyle,
                  transform,
                  transformOrigin: 'top left',
                }}
                onLoad={tilePosition === 0 ? onTileLoad : undefined}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MosaicImage;

